"use client";

import { useMemo, useState } from "react";
import {
  type CipherMode,
  CipherWorkbench,
} from "@/components/tool-kit/cipher-workbench";
import {
  Checkbox,
  DataTable,
  ErrorNotice,
  Notice,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  CARD,
  HINT,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  AFFINE_A_VALUES,
  AFFINE_KEY_COUNT,
  type AffineCandidate,
  affineBruteForce,
  affineDecode,
  affineEncode,
  affineSteps,
  isValidAffineA,
  modInverse,
} from "@/lib/tool-kit/ciphers/affine";
import {
  ALPHABET_SIZE,
  applyTextOptions,
  KEEP_ALL_TEXT,
  mod,
} from "@/lib/tool-kit/ciphers/alphabet";

const STEP_LIMIT = 12;
const BRUTE_SAMPLE = 300;
const BRUTE_PREVIEW = 80;
const BRUTE_TOP = 10;

/** English strings. A translated content file overrides these through `ui`. */
export const AFFINE_CIPHER_STRINGS = {
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  plainText: "Plain text",
  cipherText: "Cipher text",
  decodedText: "Decoded text",
  inputPlaceholder: "Type or paste your message",
  sample: "AFFINE CIPHER",
  aLabel: "a (multiplier)",
  aHint: "Must share no factor with 26 — pick one of the 12 values below.",
  bLabel: "b (shift)",
  bHint: "Any whole number; it is taken mod 26.",
  validValues: "The 12 valid values of a",
  aNotNumber: "Enter a whole number for a.",
  bNotNumber: "Enter a whole number for b.",
  aInvalid:
    "a = {a} shares a factor with 26, so two different letters would encipher to the same letter and the message could not be decoded. Choose 1, 3, 5, 7, 9, 11, 15, 17, 19, 21, 23 or 25.",
  reduced: "Taken mod 26, the key is a = {a}, b = {b}.",
  keep: "Keep",
  keepCase: "Upper and lower case",
  keepSpaces: "Spaces and line breaks",
  keepPunctuation: "Punctuation and numbers",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  swap: "Swap input and output",
  bruteForce: "Don't know the key? Try all 312 keys",
  bruteHeading: "All 312 keys, most English-looking first",
  bruteHint:
    "Ranked by how closely each result's letter frequencies match English. Reliable from a sentence or two; with only a few words, scan further down the list.",
  keyLabel: "a = {a}, b = {b}",
  useKey: "Use this key",
  showAll: "Show all 312",
  showFewer: "Show the top 10",
  working: "Working for this key",
  encodeFormula: "Encoding: E(x) = ({a}x + {b}) mod 26",
  decodeFormula: "Decoding: D(y) = {inverse}(y − {b}) mod 26",
  inverseCheck:
    "{inverse} is the inverse of {a} because {a} × {inverse} = {product}, which is 1 more than a multiple of 26.",
  inverseTable: "Every valid a and its inverse",
  inverseRow: "a⁻¹",
  stepsHeading:
    "one: Letter by letter — the first letter|other: Letter by letter — the first {count} letters",
  letter: "Letter",
  encodeRaw: "a·x + b",
  decodeRaw: "a⁻¹(y − b)",
  modColumn: "mod 26",
  result: "Result",
  noLetters: "Type some letters to see the working.",
} as const;

type Strings = typeof AFFINE_CIPHER_STRINGS;

interface Key {
  a: number;
  b: number;
  inverse: number;
}

function BruteForcePanel({
  candidates,
  strings,
  onUse,
}: {
  candidates: AffineCandidate[];
  strings: Strings;
  onUse: (candidate: AffineCandidate) => void;
}) {
  const fmt = useToolNumber();
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? candidates : candidates.slice(0, BRUTE_TOP);
  return (
    <section className={`${CARD} space-y-3`}>
      <h2 className={WIDGET_H2}>{strings.bruteHeading}</h2>
      <p className={HINT}>{strings.bruteHint}</p>
      <ol className="max-h-[480px] divide-y divide-border overflow-y-auto">
        {shown.map((candidate) => (
          <li
            className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2"
            key={`${candidate.a}-${candidate.b}`}
          >
            <span className="w-28 shrink-0 text-muted-foreground text-xs">
              {t(strings.keyLabel, {
                a: fmt(candidate.a),
                b: fmt(candidate.b),
              })}
            </span>
            <span className="min-w-0 flex-1 break-all font-mono text-foreground text-sm">
              {candidate.text.slice(0, BRUTE_PREVIEW)}
            </span>
            <button
              className={BUTTON_SMALL}
              onClick={() => onUse(candidate)}
              type="button"
            >
              {strings.useKey}
            </button>
          </li>
        ))}
      </ol>
      <button
        className={BUTTON_SMALL}
        onClick={() => setShowAll((current) => !current)}
        type="button"
      >
        {showAll ? strings.showFewer : strings.showAll}
      </button>
    </section>
  );
}

function Working({
  input,
  mode,
  cipherKey,
  strings,
}: {
  input: string;
  mode: CipherMode;
  cipherKey: Key;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const { a, b, inverse } = cipherKey;
  const steps = affineSteps(input, a, b, mode, STEP_LIMIT);
  const values = { a: fmt(a), b: fmt(b), inverse: fmt(inverse) };
  return (
    <>
      <div className="space-y-1 font-mono text-foreground text-sm">
        <p>{t(strings.encodeFormula, values)}</p>
        <p>{t(strings.decodeFormula, values)}</p>
      </div>
      <p className="text-muted-foreground text-sm leading-relaxed">
        {t(strings.inverseCheck, { ...values, product: fmt(a * inverse) })}
      </p>
      <div className="space-y-2">
        <p className={LABEL}>{strings.inverseTable}</p>
        <DataTable
          head={["a", ...AFFINE_A_VALUES.map((value) => fmt(value))]}
          rows={[
            [
              strings.inverseRow,
              ...AFFINE_A_VALUES.map((value) => fmt(modInverse(value) ?? 0)),
            ],
          ]}
        />
      </div>
      {steps.length > 0 ? (
        <div className="space-y-2">
          <p className={LABEL}>
            {tPlural(strings.stepsHeading, locale, steps.length, {
              count: fmt(steps.length),
            })}
          </p>
          <DataTable
            head={[
              strings.letter,
              mode === "encode" ? "x" : "y",
              mode === "encode" ? strings.encodeRaw : strings.decodeRaw,
              strings.modColumn,
              strings.result,
            ]}
            rows={steps.map((step) => [
              step.letter,
              fmt(step.x),
              fmt(step.raw),
              fmt(step.value),
              step.result,
            ])}
          />
        </div>
      ) : (
        <Notice>{strings.noLetters}</Notice>
      )}
    </>
  );
}

interface KeyReading {
  key?: Key;
  error?: string;
  /** The key as typed was outside 0–25 and has been reduced mod 26. */
  reduced?: boolean;
}

function readKey(aRaw: string, bRaw: string, strings: Strings): KeyReading {
  const a = parseIntegerInput(aRaw);
  const b = parseIntegerInput(bRaw);
  if (a === null) {
    return { error: strings.aNotNumber };
  }
  if (b === null) {
    return { error: strings.bNotNumber };
  }
  if (!isValidAffineA(a)) {
    return { error: t(strings.aInvalid, { a: String(a) }) };
  }
  const key: Key = {
    a: mod(a, ALPHABET_SIZE),
    b: mod(b, ALPHABET_SIZE),
    inverse: modInverse(a) ?? 1,
  };
  return { key, reduced: key.a !== a || key.b !== b };
}

export default function AffineCipherWidget() {
  const strings = useToolUi(AFFINE_CIPHER_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<CipherMode>("encode");
  const [input, setInput] = useState<string>(strings.sample);
  const [aRaw, setARaw] = useState("5");
  const [bRaw, setBRaw] = useState("8");
  const [options, setOptions] = useState(KEEP_ALL_TEXT);
  const [bruteForce, setBruteForce] = useState(false);

  const parsed = readKey(aRaw, bRaw, strings);
  const cipherKey = parsed.key;
  let output = "";
  if (cipherKey) {
    const raw =
      mode === "encode"
        ? affineEncode(input, cipherKey.a, cipherKey.b)
        : affineDecode(input, cipherKey.a, cipherKey.b);
    output = applyTextOptions(raw, options);
  }

  const sample = input.slice(0, BRUTE_SAMPLE);
  const showBrute = mode === "decode" && bruteForce && sample.trim() !== "";
  const candidates = useMemo(
    () => (showBrute ? affineBruteForce(sample) : []),
    [showBrute, sample]
  );

  const keyControls = (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.aHint}
          id="af-a"
          label={strings.aLabel}
          numeric
          onChange={setARaw}
          value={aRaw}
        />
        <TextField
          hint={strings.bHint}
          id="af-b"
          label={strings.bLabel}
          numeric
          onChange={setBRaw}
          value={bRaw}
        />
      </div>
      <fieldset className="space-y-2">
        <legend className={LABEL}>{strings.validValues}</legend>
        <div className="flex flex-wrap gap-2">
          {AFFINE_A_VALUES.map((value) => (
            <button
              aria-pressed={cipherKey?.a === value}
              className={`${BUTTON_SMALL} aria-pressed:border-primary aria-pressed:bg-primary/10`}
              key={value}
              onClick={() => setARaw(String(value))}
              type="button"
            >
              {fmt(value)}
            </button>
          ))}
        </div>
      </fieldset>
      {parsed.error ? <ErrorNotice>{parsed.error}</ErrorNotice> : null}
      {parsed.reduced && cipherKey ? (
        <p className={HINT}>
          {t(strings.reduced, { a: fmt(cipherKey.a), b: fmt(cipherKey.b) })}
        </p>
      ) : null}
      {mode === "decode" ? (
        <Checkbox
          checked={bruteForce}
          label={strings.bruteForce}
          onChange={setBruteForce}
        />
      ) : null}
    </div>
  );

  return (
    <CipherWorkbench
      extras={
        showBrute && candidates.length === AFFINE_KEY_COUNT ? (
          <BruteForcePanel
            candidates={candidates}
            onUse={(candidate) => {
              setARaw(String(candidate.a));
              setBRaw(String(candidate.b));
            }}
            strings={strings}
          />
        ) : null
      }
      idPrefix="af"
      input={input}
      keyControls={keyControls}
      labels={{
        mode: strings.mode,
        encode: strings.encode,
        decode: strings.decode,
        input: mode === "encode" ? strings.plainText : strings.cipherText,
        inputPlaceholder: strings.inputPlaceholder,
        output: mode === "encode" ? strings.cipherText : strings.decodedText,
        textOptions: strings.keep,
        keepCase: strings.keepCase,
        keepSpaces: strings.keepSpaces,
        keepPunctuation: strings.keepPunctuation,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      onInputChange={setInput}
      onModeChange={setMode}
      output={output}
      textOptions={{
        value: options,
        onChange: setOptions,
        show: ["keepCase", "keepSpaces", "keepPunctuation"],
      }}
      working={
        cipherKey ? (
          <Working
            cipherKey={cipherKey}
            input={input}
            mode={mode}
            strings={strings}
          />
        ) : null
      }
    />
  );
}
